import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInvoicesRequest } from '../../api/invoices/invoiceSlice';
import moment from 'moment';
import { DataGrid } from '@mui/x-data-grid';
import { getStatusColor, getStatusLabel } from '../../utils/statues';
import appConfig from '../../config/appConfig';

const InvoiceTable = ({ subscription, planType }) => {
  const dispatch = useDispatch();
  const { invoices, totalCount } = useSelector((state) => state.invoices);
  const stripe_subscription_id = subscription.stripe_subscription_id;
  const [page] = useState(0);
  const [limit] = useState(10);

  useEffect(() => {
    if (stripe_subscription_id) {
      dispatch(
        fetchInvoicesRequest({
          stripe_subscription_id,
          page: page + 1,
          limit,
        }),
      );
    }
  }, [dispatch, stripe_subscription_id, page, limit]);

  const handlePayment = (invoiceId, paymentUrl) => {
    if (paymentUrl) {
      window.location.href = paymentUrl;
    } else {
      alert('Payment link is not available for this invoice.');
    }
  };
  const columns = [
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (value, row) => {
        const amount = row.amount;
        return amount.toLocaleString('en-US', { style: 'currency', currency: appConfig.stripeCurrency });
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        let status = params.value;
        const dueDateUnix = params.row.due_date; // Unix timestamp

        if (status === 'open' && dueDateUnix && moment.unix(dueDateUnix).isBefore(moment())) {
          status = 'past_due';
        }

        return <Chip size={'small'} label={getStatusLabel(status)} color={getStatusColor(status)} />;
      },
    },
    {
      field: 'document_usage',
      headerName: 'Document Usage',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const documentCount = params.row.documents_count || 0;

        let usageText;
        if (planType === 'prepaid') {
          const documentsLimit = subscription?.documents_limit ?? 0;
          const percentage = documentsLimit > 0 ? Math.floor((documentCount / documentsLimit) * 100) : 0;
          usageText = `${documentCount} / ${documentsLimit} (${percentage}%)`;
        } else {
          usageText = `${documentCount} / ∞`;
        }

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">{usageText}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      flex: 1,
      valueGetter: (value, row) => {
        return row.status !== 'upcoming' && value ? moment.unix(value).tz(appConfig.timezoneName).format('lll') : '';
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 1,
      valueGetter: (value) => {
        return value ? moment(value).tz(appConfig.timezoneName).format('lll') : '';
      },
    },
    {
      field: 'actions',
      headerName: 'Payment',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return params.row.status === 'open' ? (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => handlePayment(params.row._id, params.row.invoice_payment_url)}
          >
            Pay Now
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <>
      {invoices.length === 0 ? (
        <Typography variant="body1" color="textSecondary">
          No invoices found for this subscription.
        </Typography>
      ) : (
        <DataGrid
          rows={invoices}
          columns={columns}
          getRowId={(row) => row._id}
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: limit,
              },
            },
          }}
          pageSizeOptions={[10, 25]}
          disableRowSelectionOnClick
          onPaginationModelChange={(model) => {
            dispatch(
              fetchInvoicesRequest({
                stripe_subscription_id,
                page: model.page + 1,
                limit: model.pageSize,
              }),
            );
          }}
        />
      )}
    </>
  );
};

export default InvoiceTable;
